import React, { useState, useEffect, useRef } from 'react';
import { SETTINGS, authHeaders } from '../data/dashboard/endpoints';

/**
 * MarketClock — live market session tracker
 * Shows current local time + 4 major market sessions with open/closed status
 * Designed for the DomainBar top area — compact, always visible
 * Self-contained — no props needed, updates every second
 *
 * Market sessions (UTC):
 *   Sydney:   21:00 – 07:00 UTC  (Sun–Fri)
 *   Tokyo:    00:00 – 06:00 UTC  (Mon–Sat)
 *   London:   08:00 – 16:30 UTC  (Mon–Fri)
 *   New York: 13:30 – 20:00 UTC  (Mon–Fri)
 *
 * Note: Does not account for daylight saving shifts in those cities —
 * close enough for Rexy internal use. Exact DST handling is Phase 12.
 */

interface Session {
  name: string;
  short: string;
  openUTC: number;   // minutes from midnight UTC
  closeUTC: number;  // minutes from midnight UTC
  color: string;
  activeDays: number[]; // 0=Sun, 1=Mon ... 6=Sat
}

const SESSIONS: Session[] = [
  {
    name: 'Sydney',
    short: 'SYD',
    openUTC: 21 * 60,      // 21:00 UTC previous day (wraps)
    closeUTC: 7 * 60,      // 07:00 UTC
    color: '#4ade80',      // green
    activeDays: [0, 1, 2, 3, 4], // Sun-Thu UTC (Mon-Fri Sydney)
  },
  {
    name: 'Tokyo',
    short: 'TYO',
    openUTC: 0 * 60,       // 00:00 UTC
    closeUTC: 6 * 60,      // 06:00 UTC
    color: '#f472b6',      // pink
    activeDays: [1, 2, 3, 4, 5], // Mon-Fri UTC
  },
  {
    name: 'London',
    short: 'LON',
    openUTC: 8 * 60,       // 08:00 UTC
    closeUTC: 16 * 60 + 30, // 16:30 UTC
    color: '#60a5fa',      // blue
    activeDays: [1, 2, 3, 4, 5], // Mon-Fri UTC
  },
  {
    name: 'New York',
    short: 'NYC',
    openUTC: 13 * 60 + 30, // 13:30 UTC
    closeUTC: 20 * 60,     // 20:00 UTC
    color: '#fb923c',      // orange
    activeDays: [1, 2, 3, 4, 5], // Mon-Fri UTC
  },
];

function getUTCMinutes(now: Date): number {
  return now.getUTCHours() * 60 + now.getUTCMinutes();
}

function isSessionOpen(session: Session, now: Date): boolean {
  const dayUTC = now.getUTCDay();
  if (!session.activeDays.includes(dayUTC)) return false;

  const mins = getUTCMinutes(now);

  // Sydney wraps midnight — open from 21:00 to 07:00 next day
  if (session.openUTC > session.closeUTC) {
    return mins >= session.openUTC || mins < session.closeUTC;
  }
  return mins >= session.openUTC && mins < session.closeUTC;
}

function minutesUntil(targetMins: number, currentMins: number): number {
  if (targetMins >= currentMins) return targetMins - currentMins;
  return (24 * 60 - currentMins) + targetMins;
}

function formatCountdown(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

// STAGE 2 · STEP 4 (D4): MarketClock is the DISPLAY TWIN of the backend clock
// contract — the local-time face renders the USER-SET stored timezone served by
// GET /api/rexy/settings (default Australia/Hobart). Never IP-guessed; the
// settings panel is the only writer. Market sessions stay UTC-anchored.
const DEFAULT_TZ = 'Australia/Hobart';

function formatLocalTime(now: Date, tz: string): string {
  return now.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: tz,
  });
}

function formatLocalDate(now: Date, tz: string): string {
  return now.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: tz,
  });
}

const MarketClock: React.FC = () => {
  const [now, setNow] = useState(new Date());
  const [tz, setTz] = useState(DEFAULT_TZ);
  // GHOST HOLD 2 (round 2) — the save-event PINS the zone: a slow initial GET
  // that resolves AFTER a newer save must never overwrite it (the stale-response
  // race). The ref, not state, so the in-flight GET's closure sees the pin.
  const tzPinnedRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetch(SETTINGS, { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) => {
        // Fail-safe to the default — never a guessed zone. A save that landed
        // while this GET was in flight WINS: the stale read is discarded.
        if (tzPinnedRef.current) return;
        if (j && j.success && typeof j.timezone === 'string') setTz(j.timezone);
      })
      .catch(() => { /* display twin keeps the default */ });
  }, []);

  // GHOST HOLD item 2 — the display twin follows a SAVE live: the settings panel
  // dispatches 'rexy:timezone' with the backend-confirmed zone; the clock face
  // moves without a reload. No polling, no guessing — the event carries the
  // stored value.
  useEffect(() => {
    const onTz = (e: Event) => {
      const zone = (e as CustomEvent).detail;
      if (typeof zone === 'string' && zone) {
        tzPinnedRef.current = true;
        setTz(zone);
      }
    };
    window.addEventListener('rexy:timezone', onTz);
    return () => window.removeEventListener('rexy:timezone', onTz);
  }, []);

  const currentMins = getUTCMinutes(now);

  return (
    <div className="flex items-center gap-3 ml-auto pl-3 border-l border-rexy-border/50">

      {/* Market sessions — always visible inline */}
      {SESSIONS.map(session => {
        const open = isSessionOpen(session, now);
        const nextEvent = open
          ? minutesUntil(session.closeUTC, currentMins)
          : minutesUntil(session.openUTC, currentMins);
        const countdown = formatCountdown(nextEvent);

        return (
          <div
            key={session.short}
            className="flex items-center gap-1.5"
            title={`${session.name}: ${open ? 'OPEN' : 'CLOSED'} · ${open ? 'closes' : 'opens'} in ${countdown}`}
          >
            <div
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                open ? 'animate-pulse' : 'opacity-50'
              }`}
              style={{ backgroundColor: session.color }}
            />
            <span className={`text-[9px] font-bold uppercase tracking-widest ${
              open ? 'text-rexy-muted' : 'text-rexy-muted/60'
            }`}>
              {session.short}
            </span>
            <span className={`text-[9px] font-mono ${
              open ? 'text-rexy-text' : 'text-rexy-muted/60'
            }`}>
              {countdown}
            </span>
          </div>
        );
      })}

      {/* Divider + local time */}
      <div className="flex items-center gap-1.5 pl-2 border-l border-rexy-border/50">
        <span className="text-[9px] font-mono font-bold text-rexy-text/80">
          {formatLocalTime(now, tz)}
        </span>
      </div>
    </div>
  );
};

export default MarketClock;
